import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import Header from "../components/Header";
import VirtualCard from "../components/VirtualCard";
import VietQRPanel from "../components/VietQRPanel";
import LoadFundsButton from "../components/LoadFundsButton";
import CardActivityTimeline from "../components/CardActivityTimeline";
import { api, formatUSD } from "../lib/api";
import { ArrowLeft, CheckCircle, Clock, ArrowUp, ArrowDown, Storefront, Sparkle, ChatCircleText, EnvelopeSimple, Copy, DeviceMobile } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";

export default function CardDetail() {
  const { cardId } = useParams();
  const { user } = useAuth();
  const t = useT();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [card, setCard] = useState(null);
  const [polling, setPolling] = useState(!!sessionId);

  const load = async () => {
    const r = await api.get(`/cards/${cardId}`);
    setCard(r.data);
    return r.data;
  };

  useEffect(() => { load().catch(() => toast.error(t("detail.toast.notfound"))); /* eslint-disable-next-line */ }, [cardId]);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const tick = async () => {
      if (attempts >= 8) { setPolling(false); return; }
      attempts += 1;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          await load();
          setPolling(false);
          const isLoad = sp.get("loaded") === "1";
          toast.success(isLoad ? t("detail.toast.loaded") : t("detail.toast.confirmed"));
          return;
        }
        if (r.data.status === "expired") { setPolling(false); toast.error(t("detail.toast.expired")); return; }
      } catch { /* ignore */ }
      setTimeout(tick, 2000);
    };
    tick();
    // eslint-disable-next-line
  }, [sessionId]);

  if (!card) {
    return (
      <div className="min-h-screen bg-[#FBF9F6]">
        <Header /><div className="max-w-3xl mx-auto px-6 py-20 text-[#4A5C50]">Loading…</div>
      </div>
    );
  }

  const isOwner = user && card.owner_id === user.id;
  const balance = card.balance_vnd != null ? card.balance_vnd : card.quote?.principal_vnd;
  const topups = card.topups || [];
  const charges = card.charges || [];
  const initialLoad = Number(card.quote?.principal_vnd || card.source_amount || 0);
  const topupsTotal = topups.reduce((s, t) => s + Number(t.principal_vnd || 0), 0);
  const chargesTotal = charges.reduce((s, t) => s + Number(t.amount_usd || 0), 0);

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Header />
      <div className="max-w-5xl mx-auto px-6 py-12">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#4A5C50] link-ul" data-testid="back-to-dashboard">
          <ArrowLeft size={16} /> {t("detail.back")}
        </Link>

        <div className="grid md:grid-cols-2 gap-10 mt-8">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50]">{t("detail.eyebrow")}</p>
            <h1 className="serif text-5xl mt-2">{card.receiver_name}</h1>
            <p className="text-[#4A5C50] mono text-sm mt-1">{card.receiver_email}</p>
            {card.receiver_phone && (
              <p className="text-[#4A5C50] mono text-sm mt-0.5" data-testid="detail-receiver-phone">{card.receiver_phone}</p>
            )}

            {polling && (
              <div className="mt-6 rounded-xl border border-[#D96C4A]/30 bg-[#D96C4A]/10 text-[#B04E2F] p-4 flex items-center gap-3" data-testid="polling-banner">
                <Clock size={20} weight="duotone" /> {t("detail.confirming_payment")}
              </div>
            )}
            {!polling && card.status === "active" && (
              <div className="mt-6 rounded-xl border border-[#184A30]/20 bg-[#184A30]/5 text-[#184A30] p-4 flex items-center gap-3">
                <CheckCircle size={20} weight="duotone" /> {t("detail.active_ready")}
              </div>
            )}
            {!polling && card.status === "paused" && (
              <PausedBanner card={card} canResume={isOwner || (user && user.role === "admin")} onResumed={load} />
            )}

            <TextDeliveryBanner cardId={card.id} />
            {isOwner && <DeliveryStatus card={card} onChange={load} />}

            <div className="mt-8 rounded-2xl bg-white border border-[#E5E0D8] p-6">
              <p className="text-xs tracking-[0.2em] uppercase text-[#4A5C50]">{t("detail.balance.label")}</p>
              <p className="serif text-5xl text-[#184A30] mt-1" data-testid="card-balance">{formatUSD(balance || 0)}</p>
              {card.status === "active" && isOwner && (
                <div className="mt-4">
                  <LoadFundsButton card={card} onDone={load} />
                </div>
              )}
            </div>

            <ActivityTimeline
              issuedAt={card.created_at}
              initialLoad={initialLoad}
              topups={topups}
              charges={charges}
            />

            <TopupLog
              count={topups.length}
              total={topupsTotal}
              initialLoad={initialLoad}
              topups={topups}
              scanCount={Number(card.scan_count || 0)}
              lastScannedAt={card.last_scanned_at || null}
            />

            <ChargesLog charges={charges} chargesTotal={chargesTotal} />

            {(isOwner || user?.role === "admin") && card.status === "active" && (
              <CardActivityTimeline
                cardId={card.id}
                isOwner={isOwner}
                notifyOnFirstSpend={!!card.notify_on_first_spend}
                firstSpendNotified={!!card.first_spend_notified}
              />
            )}

            {/* Re-issue CTA — appears for owners when the card has any
                spend activity (or is depleted) so the natural next step
                is one click away. Recurring "send money to family every
                month" loop without rebuilding the form from scratch. */}
            {isOwner && card.status === "active" && (charges.length > 0 || (Number(balance) || 0) <= 0) && (
              <div
                className="mt-6 rounded-2xl border border-[#D96C4A]/30 bg-gradient-to-br from-[#FFF1E8] to-[#FBF9F6] p-5"
                data-testid="reissue-cta"
              >
                <div className="flex items-start gap-4">
                  <span className="shrink-0 w-11 h-11 rounded-xl bg-[#D96C4A]/20 text-[#B04E2F] flex items-center justify-center">
                    <Sparkle size={22} weight="duotone" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="serif text-xl text-[#112318]">Send another card</p>
                    <p className="text-sm text-[#4A5C50] mt-1 leading-relaxed">
                      {(Number(balance) || 0) <= 0
                        ? `This card is spent. Re-issue ${initialLoad > 0 ? `$${initialLoad.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "a fresh card"} to ${card.receiver_name?.split(" ")[0] || "someone"} again, or pick a new recipient — same amount, fresh card.`
                        : `Happy with how this card landed? Send another one — same amount${initialLoad > 0 ? ` ($${initialLoad.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})` : ""} to a new recipient in two clicks.`}
                    </p>
                  </div>
                  <Link
                    to={`/dashboard?reissue_from=${card.id}`}
                    className="shrink-0 inline-flex items-center gap-1 rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white text-sm font-semibold px-4 py-2.5 self-center"
                    data-testid="reissue-cta-btn"
                  >
                    Re-issue
                    <ArrowLeft size={14} className="rotate-180" />
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div>
            <VirtualCard card={card} onEmailed={load} />
            {isOwner && card.status === "active" && card.card_number && (
              <RecipientNudgePanel card={card} senderName={user?.full_name} />
            )}
            {(isOwner || user?.role === "admin" || user?.role === "manager") && card.status === "active" && card.card_number && (
              <VietQRPanel card={card} onChange={load} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TopupLog({ count, total, initialLoad, topups, scanCount = 0, lastScannedAt = null }) {
  const totalReceived = +(initialLoad + total).toFixed(2);
  return (
    <div className="mt-10" data-testid="topup-log">
      <div className="bg-[#112318] text-[#FBF9F6] rounded-2xl p-6 flex items-stretch gap-6">
        <div className="flex-1">
          <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">Receiver top-up log</p>
          <p className="serif text-5xl mt-1" data-testid="topup-count">{count}</p>
          <p className="text-sm opacity-70 mt-1">
            {count === 0 ? "No top-ups yet — only the initial load on this card." : `top-up${count === 1 ? "" : "s"} applied after issuance`}
          </p>
          <div className="mt-5 pt-4 border-t border-white/10">
            <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">QR scans by receiver</p>
            <p className="mono text-2xl mt-1 text-[#8BA888]" data-testid="scan-count">{scanCount}</p>
            <p className="text-[11px] opacity-60 mt-0.5" data-testid="scan-last">
              {scanCount === 0
                ? "Receiver hasn't scanned the card yet."
                : lastScannedAt
                  ? `last scan · ${new Date(lastScannedAt).toLocaleString()}`
                  : "scan activity detected"}
            </p>
          </div>
        </div>
        <div className="border-l border-white/10 pl-6 flex flex-col justify-center gap-3">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">Initial load</p>
            <p className="mono mt-1" data-testid="topup-initial">{formatUSD(initialLoad)}</p>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">Top-ups total</p>
            <p className="mono mt-1" data-testid="topup-total">{formatUSD(total)}</p>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">Lifetime received</p>
            <p className="mono mt-1 text-[#8BA888]" data-testid="topup-lifetime">{formatUSD(totalReceived)}</p>
          </div>
        </div>
      </div>

      {topups.length > 0 && (
        <div className="mt-4 divide-y divide-[#E5E0D8] bg-white border border-[#E5E0D8] rounded-2xl" data-testid="topup-history">
          {topups.slice().reverse().map((t, i) => {
            const isBank = (t.payment_method || "").toLowerCase().includes("bank");
            const sourceTail = (t.source_label || "").replace(/^(Bank|Debit card)\s*/i, "").trim();
            return (
              <div key={t.id} className="flex items-center justify-between p-4 text-sm" data-testid={`topup-row-${t.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#184A30]/10 flex items-center justify-center text-[#184A30] mono text-xs font-semibold">
                    #{topups.length - i}
                  </div>
                  <div>
                    <p className="font-medium text-[#112318] flex items-center gap-2">
                      <ArrowUp size={12} weight="bold" className="text-[#184A30]" />
                      {isBank ? "Bank transfer" : "Debit card"}
                      {sourceTail && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#184A30]/5 border border-[#184A30]/15 text-[10px] tracking-[0.15em] uppercase text-[#184A30]">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${isBank ? "bg-[#184A30]" : "bg-[#CBA35C]"}`} />
                          <span className="mono tracking-normal normal-case">{sourceTail}</span>
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-[#4A5C50] mono">
                      {new Date(t.ts).toLocaleString()}
                      {t.holder_name ? <> · {t.holder_name}</> : null}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="mono text-[#184A30] font-semibold">+ {formatUSD(t.principal_vnd)}</p>
                  <p className="text-xs text-[#4A5C50] mono">paid {formatUSD(t.amount_source)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function ChargesLog({ charges, chargesTotal }) {
  return (
    <div className="mt-8" data-testid="charges-log">
      <div className="rounded-2xl bg-white border border-[#E5E0D8] p-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#D96C4A]/15 flex items-center justify-center">
            <Storefront size={18} weight="duotone" className="text-[#D96C4A]" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Merchant charges · QR pay</p>
            <p className="serif text-3xl mt-0.5 text-[#112318]" data-testid="charges-count">
              {charges.length} <span className="text-[#4A5C50] text-base">charge{charges.length === 1 ? "" : "s"}</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Spent</p>
            <p className="mono mt-0.5 text-[#D96C4A] font-semibold" data-testid="charges-total">− {formatUSD(chargesTotal)}</p>
          </div>
        </div>

        {charges.length > 0 ? (
          <div className="mt-5 divide-y divide-[#E5E0D8]" data-testid="charges-history">
            {charges.slice().reverse().map((c, i) => (
              <div key={c.id} className="flex items-center justify-between py-3 text-sm" data-testid={`charge-row-${c.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#D96C4A]/10 flex items-center justify-center text-[#D96C4A]">
                    <ArrowDown size={12} weight="bold" />
                  </div>
                  <div>
                    <p className="font-medium text-[#112318] flex items-center gap-2">
                      {c.merchant_name}
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D96C4A]/10 border border-[#D96C4A]/20 text-[10px] tracking-[0.15em] uppercase text-[#B04E2F]">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#D96C4A]" />
                        QR scan
                      </span>
                    </p>
                    <p className="text-xs text-[#4A5C50] mono">
                      {new Date(c.ts).toLocaleString()}
                      {c.memo ? <> · {c.memo}</> : null}
                    </p>
                  </div>
                </div>
                <p className="mono font-semibold text-[#D96C4A]">− {formatUSD(c.amount_usd)}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-[#4A5C50] mt-3 mono">No QR payments yet — the receiver hasn't spent from this card.</p>
        )}
      </div>
    </div>
  );
}

function PausedBanner({ card, canResume, onResumed }) {
  const [busy, setBusy] = React.useState(false);
  const resume = async () => {
    setBusy(true);
    try {
      await api.post(`/cards/${card.id}/unpause`);
      toast.success("Card resumed. Charges are enabled again.");
      await onResumed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume card");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-6 rounded-xl border border-[#D96C4A]/40 bg-[#D96C4A]/10 p-4" data-testid="paused-banner">
      <div className="flex items-start gap-3">
        <Clock size={20} weight="duotone" className="text-[#B04E2F] mt-0.5" />
        <div className="flex-1">
          <p className="text-[#B04E2F] font-medium">Card is paused</p>
          <p className="text-xs text-[#4A5C50] mt-1">
            The card was frozen{card.paused_at ? ` on ${new Date(card.paused_at).toLocaleString()}` : ""}
            {card.paused_reason === "receiver_freeze_link" ? " via receiver purchase-alert link"
              : card.paused_reason === "auto_velocity_freeze" ? " automatically after detecting unusual spending velocity"
              : ""}.
            No charges can go through until it's resumed.
          </p>
        </div>
        {canResume && (
          <button
            onClick={resume}
            disabled={busy}
            className="h-9 px-4 rounded-lg bg-[#184A30] hover:bg-[#0F3320] text-[#FBF9F6] text-xs font-medium disabled:opacity-50"
            data-testid="resume-card-btn"
          >
            {busy ? "Resuming…" : "Resume card"}
          </button>
        )}
      </div>
    </div>
  );
}

function ActivityTimeline({ issuedAt, initialLoad, topups = [], charges = [] }) {
  // Build unified event list (chronological ascending) so we can compute running balance.
  const events = [];
  if (issuedAt && Number(initialLoad) > 0) {
    events.push({
      kind: "issue",
      ts: issuedAt,
      amount: Number(initialLoad),
      label: "Card issued",
      sub: "Initial deposit activated the card",
    });
  }
  topups.forEach((t) => {
    const isBank = (t.payment_method || "").toLowerCase().includes("bank");
    const sourceTail = (t.source_label || "").replace(/^(Bank|Debit card)\s*/i, "").trim();
    events.push({
      kind: "topup",
      ts: t.ts,
      amount: Number(t.principal_vnd || 0),
      label: isBank ? "Bank transfer top-up" : "Debit card top-up",
      sub: [sourceTail, t.holder_name].filter(Boolean).join(" · ") || null,
      id: t.id,
    });
  });
  charges.forEach((c) => {
    events.push({
      kind: "charge",
      ts: c.ts,
      amount: Number(c.amount_usd || 0),
      label: c.merchant_name || "Merchant charge",
      sub: c.memo || "QR scan payment",
      id: c.id,
    });
  });

  events.sort((a, b) => new Date(a.ts) - new Date(b.ts));

  let running = 0;
  const withBalance = events.map((e) => {
    running += e.kind === "charge" ? -e.amount : e.amount;
    return { ...e, balance: +running.toFixed(2) };
  });

  const ordered = withBalance.slice().reverse(); // newest first for display
  const totalIn = +(Number(initialLoad) + topups.reduce((s, t) => s + Number(t.principal_vnd || 0), 0)).toFixed(2);
  const totalOut = +charges.reduce((s, c) => s + Number(c.amount_usd || 0), 0).toFixed(2);

  if (ordered.length === 0) return null;

  return (
    <div className="mt-10" data-testid="activity-timeline">
      <div className="flex items-end justify-between mb-4">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Activity timeline</p>
          <p className="serif text-2xl text-[#112318] mt-0.5">Lifetime ledger</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">In · Out</p>
          <p className="mono text-sm mt-0.5">
            <span className="text-[#184A30] font-semibold">+ {formatUSD(totalIn)}</span>
            <span className="text-[#4A5C50] mx-1.5">·</span>
            <span className="text-[#D96C4A] font-semibold">− {formatUSD(totalOut)}</span>
          </p>
        </div>
      </div>

      <div className="bg-white border border-[#E5E0D8] rounded-2xl p-5">
        <ol className="relative">
          {/* vertical rail */}
          <span className="absolute left-[19px] top-2 bottom-2 w-px bg-gradient-to-b from-[#184A30]/30 via-[#E5E0D8] to-[#D96C4A]/30" aria-hidden="true" />
          {ordered.map((e, idx) => {
            const isIn = e.kind !== "charge";
            const color = e.kind === "issue" ? "#CBA35C" : e.kind === "topup" ? "#184A30" : "#D96C4A";
            const bg = e.kind === "issue" ? "#CBA35C" : e.kind === "topup" ? "#184A30" : "#D96C4A";
            const Icon = e.kind === "issue" ? Sparkle : isIn ? ArrowUp : ArrowDown;
            const dateStr = e.ts ? new Date(e.ts).toLocaleString() : "";
            return (
              <li
                key={`${e.kind}-${e.id || idx}-${e.ts}`}
                className="relative pl-12 py-3 flex items-center justify-between gap-4"
                data-testid={`timeline-row-${e.kind}-${e.id || idx}`}
              >
                <span
                  className="absolute left-[8px] top-1/2 -translate-y-1/2 w-[22px] h-[22px] rounded-full flex items-center justify-center text-white shadow-sm"
                  style={{ backgroundColor: bg }}
                  aria-hidden="true"
                >
                  <Icon size={11} weight="bold" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#112318] truncate">{e.label}</p>
                  <p className="text-[11px] text-[#4A5C50] mono mt-0.5">
                    {dateStr}
                    {e.sub ? <> · {e.sub}</> : null}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="mono font-semibold text-sm" style={{ color }}>
                    {isIn ? "+" : "−"} {formatUSD(e.amount)}
                  </p>
                  <p className="text-[10px] text-[#4A5C50] mono mt-0.5">
                    bal {formatUSD(e.balance)}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}


function TextDeliveryBanner({ cardId }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = React.useCallback(() => {
    api.get(`/cards/${cardId}/text-history`).then((r) => setState(r.data)).catch(() => {});
  }, [cardId]);

  useEffect(() => { load(); }, [load]);

  const resend = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/cards/${cardId}/text-resend`);
      toast.info(`Retried · Twilio status: ${r.data.status}. Checking delivery in 8s…`, { duration: 10000 });
      setTimeout(async () => {
        try {
          const s = await api.get(`/cards/${cardId}/text-status/${r.data.sid}`);
          const d = s.data;
          if (d.delivered) {
            toast.success(`✓ Delivered to ${r.data.to_phone}`, { duration: 8000 });
          } else if (d.fallback?.fired) {
            toast.success(
              `✓ Re-sent as text-only SMS — image attachment rejected, card details are in the message body. New status: ${d.fallback.status}.`,
              { duration: 18000 }
            );
          } else if (d.final) {
            toast.error(`❌ Still failing — ${d.fix_hint || d.error_message || d.status}`, { duration: 20000 });
          }
          load();
        } catch (_) {}
      }, 8000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setBusy(false);
    }
  };

  if (!state || !state.last_failed) return null;
  const last = state.history[0];

  return (
    <div
      className="mt-6 rounded-xl border border-[#D96C4A]/40 bg-[#D96C4A]/10 p-5"
      data-testid="text-delivery-banner"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#B04E2F]">Text not delivered</p>
          <p className="text-sm font-medium text-[#112318] mt-1">
            The text to <span className="mono">{last.to_phone}</span> was rejected by the carrier.
          </p>
          <p className="text-xs text-[#4A5C50] mt-2 leading-relaxed" data-testid="text-delivery-hint">
            {state.last_fix_hint || `${last.error_code ? `Error ${last.error_code}: ` : ""}${last.error_message || last.status}`}
          </p>
          <p className="text-[11px] text-[#8C6B21] mt-2 mono">
            Sent {last.ts ? new Date(last.ts).toLocaleString() : ""} · status {last.status}
          </p>
        </div>
        <button
          type="button"
          onClick={resend}
          disabled={busy}
          className="px-4 py-2 rounded-lg bg-[#184A30] hover:bg-[#0F3320] text-white text-sm font-medium disabled:opacity-50"
          data-testid="text-delivery-resend-btn"
        >
          {busy ? "Resending…" : "Resend text"}
        </button>
      </div>
    </div>
  );
}


function DeliveryStatus({ card, onChange }) {
  const t = useT();
  const [saving, setSaving] = useState(false);
  const enabled = !!card.notify_on_delivery;
  const deliveredAt = card.delivered_at;
  const receiverFirst = (card.receiver_name || "").split(" ")[0] || t("detail.delivery.receiver_fallback");

  const toggle = async () => {
    setSaving(true);
    try {
      await api.patch(`/cards/${card.id}/notify-on-delivery`, { enabled: !enabled });
      toast.success(!enabled ? t("detail.delivery.toast_enabled") : t("detail.delivery.toast_disabled"));
      if (onChange) await onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("detail.delivery.toast_failed"));
    } finally {
      setSaving(false);
    }
  };

  if (deliveredAt) {
    let when = "";
    try {
      when = new Date(deliveredAt).toLocaleString(undefined, {
        dateStyle: "medium", timeStyle: "short",
      });
    } catch { when = deliveredAt; }
    return (
      <div
        className="mt-4 rounded-xl border border-[#184A30]/20 bg-[#184A30]/5 text-[#184A30] p-4 flex items-start gap-3"
        data-testid="delivery-confirmed-banner"
      >
        <CheckCircle size={20} weight="duotone" className="shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold">
            {t("detail.delivery.received_title", { name: receiverFirst })}
          </p>
          <p className="text-[#4A5C50] mt-0.5 text-[12px]">
            {t("detail.delivery.received_when", { when })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-4 rounded-xl border border-[#E5E0D8] bg-white p-4 flex items-start gap-3"
      data-testid="delivery-toggle-row"
    >
      <Sparkle size={20} weight="duotone" className="text-[#184A30] shrink-0 mt-0.5" />
      <div className="flex-1 text-sm">
        <p className="font-semibold text-[#112318]">
          {enabled ? t("detail.delivery.waiting_title_on") : t("detail.delivery.waiting_title_off")}
        </p>
        <p className="text-[#4A5C50] mt-0.5 text-[12px]">
          {enabled ? t("detail.delivery.waiting_body_on") : t("detail.delivery.waiting_body_off")}
        </p>
      </div>
      <button
        type="button"
        onClick={toggle}
        disabled={saving}
        className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors disabled:opacity-50 ${
          enabled
            ? "bg-[#184A30] text-white hover:bg-[#0F3320]"
            : "bg-[#E5E0D8] text-[#112318] hover:bg-[#CBA35C]/30"
        }`}
        data-testid="delivery-toggle-btn"
      >
        {saving ? "…" : enabled ? t("detail.delivery.toggle_on") : t("detail.delivery.toggle_off")}
      </button>
    </div>
  );
}

function RecipientNudgePanel({ card, senderName }) {
  const t = useT();
  const last4 = (card.card_number || "").slice(-4);
  const exp = card.expiry || "";
  const receiverFirst = (card.receiver_name || "").split(" ")[0] || t("nudge.receiver_fallback");
  const senderFirst = (senderName || "").split(" ")[0] || t("nudge.sender_fallback");
  const amount = Number(card.quote?.principal_vnd || card.source_amount || 0);
  const amountStr = formatUSD(amount);

  const payUrl = (() => {
    const base = (typeof window !== "undefined" && window.location?.origin) || "";
    const encExp = encodeURIComponent(exp);
    return `${base}/pay?last4=${last4}&exp=${encExp}&s=scan`;
  })();

  const message = t("nudge.template", {
    receiver: receiverFirst,
    sender: senderFirst,
    amount: amountStr,
    url: payUrl,
  });

  const subject = t("nudge.email_subject", { sender: senderFirst, amount: amountStr });

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success(t("nudge.toast_copied"));
    } catch {
      toast.error(t("nudge.toast_copy_failed"));
    }
  };

  const smsHref = card.receiver_phone
    ? `sms:${card.receiver_phone}${/(iphone|ipad|ipod)/i.test(navigator.userAgent) ? "&" : "?"}body=${encodeURIComponent(message)}`
    : null;

  const mailHref = card.receiver_email
    ? `mailto:${card.receiver_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`
    : null;

  return (
    <div
      className="mt-6 rounded-2xl border border-[#E5E0D8] bg-white p-5"
      data-testid="recipient-nudge-panel"
    >
      <div className="flex items-center gap-2">
        <ChatCircleText size={18} weight="duotone" className="text-[#184A30]" />
        <p className="text-xs tracking-[0.25em] uppercase text-[#4A5C50]">
          {t("nudge.eyebrow")}
        </p>
      </div>
      <h3 className="serif text-xl mt-2 text-[#112318]">{t("nudge.title")}</h3>
      <p className="text-[13px] text-[#4A5C50] mt-1">
        {t("nudge.subtitle", { receiver: receiverFirst })}
      </p>

      <div
        className="mt-4 rounded-xl bg-[#FBF9F6] border border-[#E5E0D8] p-3 text-[13px] text-[#112318] whitespace-pre-wrap mono"
        data-testid="recipient-nudge-message"
      >
        {message}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={copyText}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#184A30] hover:bg-[#0F3320] text-white text-xs font-medium transition-colors"
          data-testid="recipient-nudge-copy"
        >
          <Copy size={14} weight="duotone" /> {t("nudge.action_copy")}
        </button>
        {smsHref && (
          <a
            href={smsHref}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E5E0D8] hover:bg-[#CBA35C]/30 text-[#112318] text-xs font-medium transition-colors"
            data-testid="recipient-nudge-sms"
          >
            <DeviceMobile size={14} weight="duotone" /> {t("nudge.action_sms")}
          </a>
        )}
        {mailHref && (
          <a
            href={mailHref}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E5E0D8] hover:bg-[#CBA35C]/30 text-[#112318] text-xs font-medium transition-colors"
            data-testid="recipient-nudge-email"
          >
            <EnvelopeSimple size={14} weight="duotone" /> {t("nudge.action_email")}
          </a>
        )}
      </div>
    </div>
  );
}

